import { AutocompleteRequest, AutocompleteResponse, HawksearchGlobal, RawAutocompleteRequest, RawAutocompleteResponse } from '../models';
import { BaseService } from './base.service';

declare let Hawksearch: HawksearchGlobal;

export class AutocompleteService extends BaseService {
    protected baseUrl = Hawksearch.config.endpoints?.search || 'https://searchapi-dev.hawksearch.net/api/v2';

    // #region Search Operations

    async query(query: string): Promise<AutocompleteResponse> {
        const request: AutocompleteRequest = {
            query: query
        };

        const response = await this.executeAutocomplete(request);

        return response;
    }

    private executeAutocomplete(request: AutocompleteRequest): Promise<AutocompleteResponse> {
        const rawRequest: RawAutocompleteRequest = {
            ClientGuid: Hawksearch.config.clientId,
            Keyword: request.query,
            DisplayFullResponse: true
        };

        return this.httpPost<RawAutocompleteResponse>('/autocomplete', rawRequest).then((rawSearchResults) => {
            const autocompleteResponse = this.convertAutocompleteResponse(request.query, rawSearchResults);

            this.bindComponent(autocompleteResponse);

            return autocompleteResponse;
        });
    }

    private convertAutocompleteResponse(query: string, autocompleteResponse: RawAutocompleteResponse): AutocompleteResponse {
        return {
            query: query,
            categories: {
                title: autocompleteResponse.CategoryHeading,
                results: autocompleteResponse.Categories.map((c) => ({
                    title: this.stripHtml(c.Value),
                    field: c.FieldQSName,
                    value: c.FieldQSValue,
                    url: `?${this.queryStringParams.facet}-${c.FieldQSName}=${encodeURIComponent(c.FieldQSValue)}`
                }))
            },
            content: {
                title: autocompleteResponse.ContentHeading,
                results: autocompleteResponse.Content.map((c) => ({
                    id: c.Results.DocId,
                    pinned: c.Results.IsPin,
                    score: c.Results.Score,
                    attributes: c.Results.Document,
                    title: this.stripHtml(c.Value),
                    url: c.Url
                })),
                totalRecords: autocompleteResponse.ContentCount
            },
            queries: {
                title: autocompleteResponse.PopularHeading,
                results: autocompleteResponse.Popular.map((r) => ({
                    query: this.stripHtml(r.RawValue),
                    url: `?${this.queryStringParams.query}=${encodeURIComponent(this.stripHtml(r.RawValue))}`
                }))
            },
            products: {
                title: autocompleteResponse.ProductHeading,
                results: autocompleteResponse.Products.map((p) => ({
                    id: p.Results.DocId,
                    pinned: p.Results.IsPin,
                    score: p.Results.Score,
                    attributes: p.Results.Document,
                    title: this.stripHtml(p.ProductName),
                    sku: p.Sku || undefined,
                    imageUrl: p.Thumb?.Url,
                    url: p.Url
                })),
                totalRecords: autocompleteResponse.ProductCount
            },
            totalRecords: autocompleteResponse.Count,
            viewAllText: autocompleteResponse.ViewAllButtonLabel
        };
    }

    // #endregion Search Operations

    // #region Events

    bindComponent(autocompleteResponse: AutocompleteResponse): void {
        this.triggerEvent('hawksearch:bind-autocomplete', autocompleteResponse);
    }

    private triggerEvent<T>(name: string, data: T): void {
        const event = new CustomEvent(name, { detail: data });

        dispatchEvent(event);
    }

    // #endregion Events
}

export const autocompleteService = new AutocompleteService();
